import { lazy, Suspense, useEffect } from 'react'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'

import { useAuth } from 'common/auth/auth-provider'
import ErrorBoundary from 'common/error-boundary'
import { getTenantBasename } from 'common/tenant'
import { getHostRole } from 'common/tenant/host'
import CustomDomainTracker from 'common/tenant/custom-domain-tracker'
import TenantGuard from 'common/tenant/tenant-guard'
import { landingUrl } from 'common/tenant/url'
import { PRODUCTION_HOSTNAMES } from 'common/utils/google-analytics'

import Backdrop from 'components/ui/backdrop'

import NotFound from 'components/admin/not-found'

import AnonymousRoute from './anonymous-route'
import BackofficeRoute from './backoffice-route'
import PrivateRoute from './private-route'
import { getDefaultAuthenticatedRoute, getRedirectRoute } from './redirect'
//import AccountRoute from './account-route'

// Componente para rastrear mudanças de página no Google Analytics
function GoogleAnalyticsTracker() {
  const location = useLocation()

  useEffect(() => {
    // Verifica se está em produção
    const isProduction = PRODUCTION_HOSTNAMES.includes(window.location.hostname)

    if (isProduction && window.gtag) {
      // Rastreia a visualização da página
      window.gtag('config', 'G-FZSX6MDGXJ', {
        page_path: location.pathname + location.search
      })
    }
  }, [location])

  return null
}

const AdminLayout = lazy(() => import('layouts/admin'))
const PortalLayout = lazy(() => import('layouts/portal'))
const CaptadorLayout = lazy(() => import('layouts/captador'))

const PortalDashboard = lazy(() => import('pages/portal/dashboard'))
const PortalRealEstates = lazy(() => import('pages/portal/real-estates'))
const PortalContracts = lazy(() => import('pages/portal/contracts'))
const PortalContractDetail = lazy(() => import('pages/portal/contracts/detail'))
const PortalProposals = lazy(() => import('pages/portal/proposals'))
const PortalDocuments = lazy(() => import('pages/portal/documents'))
const PortalMyData = lazy(() => import('pages/portal/my-data'))

const CaptadorDashboard = lazy(() => import('pages/captador/dashboard'))
const CaptadorNovoImovel = lazy(() => import('pages/captador/novo-imovel'))

const Login = lazy(() => import('pages/login'))
const ChangePassword = lazy(() => import('pages/change-password'))

const Dashboard = lazy(() => import('pages/admin/dashboard'))

const EmployeeDetail = lazy(() => import('pages/admin/employees/detail'))
const EmployeesList = lazy(() => import('pages/admin/employees/list'))

const UserDetail = lazy(() => import('pages/admin/users/detail'))
const UsersList = lazy(() => import('pages/admin/users/list'))
const UserCreate = lazy(() => import('pages/admin/users/new'))
const UserEdit = lazy(() => import('pages/admin/users/edit'))

const AccessList = lazy(() => import('pages/admin/access/list'))
const AccessDetail = lazy(() => import('pages/admin/access/detail'))
const AccessSummary = lazy(() => import('pages/admin/access/summary'))

const ContractDetail = lazy(() => import('pages/admin/contracts/detail'))
const ContractsList = lazy(() => import('pages/admin/contracts/list'))
const ContractPDF = lazy(() => import('pages/admin/contracts/pdf'))

const ProposalDetail = lazy(() => import('pages/admin/proposals/detail'))
const ProposalsList = lazy(() => import('pages/admin/proposals/list'))
const ProposalsPipeline = lazy(() => import('pages/admin/proposals/pipeline'))
const ProposalsCompare = lazy(() => import('pages/admin/proposals/compare'))

const Conversations = lazy(() => import('pages/admin/conversations'))
const SectorsList = lazy(() => import('pages/admin/sectors'))
const HomeSectionsList = lazy(() => import('pages/admin/home-sections/list'))
const CustomPagesList = lazy(() => import('pages/admin/custom-pages/list'))
const CustomPagesForm = lazy(() => import('pages/admin/custom-pages/form'))
const ApiKeysList = lazy(() => import('pages/admin/integration/api-keys'))
const WebhooksList = lazy(() => import('pages/admin/integration/webhooks'))
const IntegrationDocs = lazy(() => import('pages/admin/integration/docs'))
const PortaisIntegracao = lazy(() => import('pages/admin/integration/portals'))
const GtmIntegration = lazy(() => import('pages/admin/integration/gtm'))
const MarciaIntegracao = lazy(() => import('pages/admin/integration/marcia'))
const ContractTemplatesList = lazy(
  () => import('pages/admin/contract-templates')
)
const EmailTemplatesPage = lazy(() => import('pages/admin/email-templates'))
const TutorialsList = lazy(() => import('pages/admin/tutorials/list'))
const TutorialDetail = lazy(() => import('pages/admin/tutorials/detail'))

const LandingPage = lazy(() => import('pages/landing'))

const SignPage = lazy(() => import('pages/sign'))
const SignContractPdfPage = lazy(() => import('pages/sign/contract-pdf'))
const SignedDocumentPage = lazy(() => import('pages/sign/signed-document'))
const ProposalResponsePage = lazy(() => import('pages/proposal-response'))

const PropertyDetail = lazy(() => import('pages/admin/properties/detail'))
const PropertyOverview = lazy(() => import('pages/admin/properties/overview'))

const PropertiesList = lazy(() => import('pages/admin/properties/list'))
const PropertiesApprovals = lazy(
  () => import('pages/admin/properties/approvals')
)
const ImageGroupList = lazy(() => import('pages/admin/image-groups'))
const ImageGroupDetail = lazy(() => import('pages/admin/image-groups/detail'))
const PublicUpload = lazy(() => import('pages/public-upload'))

const CondominiumList = lazy(() => import('pages/admin/condominium/list'))
const CondominiumCreate = lazy(() => import('pages/admin/condominium/create'))
const CondominiumSee = lazy(() => import('pages/admin/condominium/see'))

const EmpreendimentoDetail = lazy(
  () => import('pages/admin/empreendimentos/detail')
)
const PropertiesSummary = lazy(() => import('pages/admin/properties/summary'))
const PropertiesNew = lazy(
  () => import('pages/admin/properties/detail/edit-property')
)

const LeadsGestao = lazy(() => import('pages/admin/leads/gestao'))
const LeadsHistorico = lazy(() => import('pages/admin/leads/historico'))
const BrokerProductivity = lazy(() => import('pages/admin/leads/produtividade'))
const LeadDetail = lazy(() => import('pages/admin/leads/detail'))
const LeadRoulette = lazy(() => import('pages/admin/leads/roleta'))
const LeadOriginKeywords = lazy(
  () => import('pages/admin/lead-origin-keywords')
)
const Agenda = lazy(() => import('pages/admin/agenda'))
const Tasks = lazy(() => import('pages/admin/tasks'))

const ServiceTicketList = lazy(() => import('pages/admin/service-tickets/list'))
const ServiceTicketDetail = lazy(
  () => import('pages/admin/service-tickets/detail')
)
const MyTickets = lazy(() => import('pages/admin/my-tickets'))

const MyAccount = lazy(() => import('pages/admin/my-account/detail'))

const Agency = lazy(() => import('pages/admin/agency'))
const Customize = lazy(() => import('pages/admin/customize'))

const SiteLayout = lazy(() => import('layouts/site'))

const Home = lazy(() => import('pages/site/home'))
const PropertyClientDetail = lazy(() => import('pages/site/property-detail'))
const Favorite = lazy(() => import('pages/site/favorites'))
const Announce = lazy(() => import('pages/site/announce'))
const KnowUs = lazy(() => import('pages/site/know-us'))
const Terms = lazy(() => import('pages/site/terms'))
const Privacy = lazy(() => import('pages/site/privacy'))
const CustomPageView = lazy(() => import('pages/site/custom-page'))
const EmpreendimentoPublic = lazy(
  () => import('pages/site/empreendimento-detail')
)
const EmpreendimentosPublic = lazy(() => import('pages/site/empreendimentos'))
const Search = lazy(() => import('pages/site/search'))
const Register = lazy(() => import('pages/site/register'))
const RegisterAgency = lazy(() => import('pages/site/register-agency'))
const ClientGateway = lazy(() => import('pages/site/client-gateway'))

const PendingApprovalNoticeById = lazy(() =>
  import('components/admin/pending-approval-notice').then((m) => ({
    default: m.PendingApprovalNoticeById
  }))
)

// Usado em `/admin/properties/edit/:id`. Imovel enviado pelo captador e ainda
// pendente mostra o aviso com o botao Aprovar acima do formulario (#CAPT).
function PropertiesNewRouteElement() {
  const navigate = useNavigate()
  const { id = '' } = useParams()
  return (
    <>
      <PendingApprovalNoticeById id={id} sx={{ mx: 2, mt: 1 }} />
      <PropertiesNew onClose={() => navigate('/admin')} onSuccess={() => {}} />
    </>
  )
}

/**
 * Manda o navegador para fora da aplicacao.
 *
 * `<Navigate>` so resolve rotas internas; aqui o destino e outro host da
 * plataforma, entao quem redireciona e o proprio navegador.
 */
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to)
  }, [to])

  return <Backdrop />
}

/**
 * Rotas dos hosts que pertencem a plataforma, e nao a uma imobiliaria.
 *
 * `lp.seulugar.imb.br` publica a captacao e o cadastro na raiz do host — as
 * mesmas telas que hoje moram em `/para-imobiliarias`, sem nenhuma pagina nova.
 * `cliente.seulugar.imb.br/{slug}` so traduz o slug no endereco do painel.
 *
 * Nenhum dos dois passa pelo `TenantGuard`: nao ha imobiliaria no endereco para
 * validar, e o basename e sempre a raiz.
 */
function PlatformRouter({ role }: { role: 'landing' | 'client-gateway' }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Backdrop />}>
        <BrowserRouter>
          <GoogleAnalyticsTracker />
          {role === 'landing' ? (
            <Routes>
              {/* Sem `SiteLayout` de proposito: o cabecalho do site aponta para
                  `/explore`, `/portal` e `/login`, rotas que nao existem neste
                  host — o visitante clicaria e voltaria para a propria landing.
                  A landing ja traz a navegacao que faz sentido aqui. */}
              <Route path='/' element={<LandingPage />} />
              <Route path='/cadastro' element={<RegisterAgency />} />
              <Route path='/termos' element={<Terms />} />
              <Route path='/privacidade' element={<Privacy />} />
              {/* O formulario de cadastro e o mesmo componente nos dois hosts e
                  linka para `/terms` e `/privacy` — os nomes que existem na
                  arvore principal. Sem estes dois apelidos aqui, os links do
                  aceite caem no `*` e abrem a landing no lugar do documento. */}
              <Route path='/terms' element={<Terms />} />
              <Route path='/privacy' element={<Privacy />} />
              {/* Enderecos antigos, agora servidos pela raiz deste host */}
              <Route
                path='/para-imobiliarias'
                element={<Navigate to='/' replace />}
              />
              <Route
                path='/para-imobiliarias/cadastro'
                element={<Navigate to='/cadastro' replace />}
              />
              <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path='/:slug' element={<ClientGateway />} />
              <Route
                path='*'
                element={<ExternalRedirect to={landingUrl()} />}
              />
            </Routes>
          )}
        </BrowserRouter>
      </Suspense>
    </ErrorBoundary>
  )
}

export default function Router() {
  const auth = useAuth()
  const isAuthenticated = auth.isAuthenticated()

  // Verifica e redireciona se necessário, mas só quando necessário
  useEffect(() => {
    // Só verifica se há dados de autenticação e se não estamos na rota de login
    if (auth.auth && window.location.pathname !== '/login') {
      auth.checkAndRedirectIfInvalid()
    }
  }, [auth.auth])

  const redirectRoute = getRedirectRoute(isAuthenticated)

  // Os hosts da plataforma tem arvore de rotas propria. A checagem fica depois
  // dos hooks de proposito: o papel do host nao muda enquanto a pagina vive,
  // entao a ordem dos hooks continua estavel.
  const hostRole = getHostRole()

  if (hostRole === 'landing' || hostRole === 'client-gateway') {
    return <PlatformRouter role={hostRole} />
  }

  return (
    <ErrorBoundary>
      <TenantGuard>
        <Suspense fallback={<Backdrop />}>
          {/*
          O basename e o slug da imobiliaria quando a URL traz um — assim todo
          `Link to='/explore'` e `navigate('/property/:id')' do restante da
          aplicacao continua igual, e o React Router acrescenta o prefixo. Sem
          slug na URL, o basename e indefinido e o site roda na raiz como sempre.
        */}
          <BrowserRouter basename={getTenantBasename()}>
            <GoogleAnalyticsTracker />
            <CustomDomainTracker />
            <Routes>
              {/*Admin Pages*/}
              <Route
                path={'/admin'}
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    redirectTo='/login'
                  >
                    <AdminLayout />
                  </PrivateRoute>
                }
              >
                <Route
                  index
                  element={
                    auth.isEmployee ? (
                      <Dashboard />
                    ) : auth.isCaptador ? (
                      <Navigate to='/captador' replace />
                    ) : auth.isCustomer ? (
                      <Navigate to='/portal' replace />
                    ) : (
                      <MyAccount />
                    )
                  }
                />
                {/* Gestao de imovel, condominio e empreendimento fica dentro do
                    guard: sao telas internas do CRM. Fora dele, qualquer usuario
                    autenticado — cliente do portal, captador — chegava nelas
                    digitando a URL. Captador usa /captador, cliente usa /portal. */}
                {auth.isEmployee ? (
                  <>
                    <Route path='properties'>
                      <Route path='' element={<PropertiesList />} />
                      <Route
                        path='approvals'
                        element={<PropertiesApprovals />}
                      />
                      <Route path=':id' element={<PropertyDetail />} />
                      <Route
                        path='summary/:id'
                        element={<PropertiesSummary />}
                      />
                      <Route
                        path='overview/:id'
                        element={<PropertyOverview />}
                      />
                      <Route
                        path='edit/:id'
                        element={<PropertiesNewRouteElement />}
                      />
                    </Route>
                    <Route path='condominium'>
                      <Route path='' element={<CondominiumList />} />
                      <Route path=':id' element={<CondominiumCreate />} />
                      <Route path='see/:id' element={<CondominiumSee />} />
                    </Route>
                    <Route path='empreendimento'>
                      <Route path=':id' element={<EmpreendimentoDetail />} />
                    </Route>
                    <Route path='leads'>
                      <Route path='gestao' element={<LeadsGestao />} />
                      <Route path='gestao/:id' element={<LeadDetail />} />
                      <Route path='historico' element={<LeadsHistorico />} />
                      <Route
                        path='produtividade'
                        element={<BrokerProductivity />}
                      />
                      <Route path='roleta' element={<LeadRoulette />} />
                    </Route>
                    <Route path='agenda' element={<Agenda />} />
                    <Route path='tarefas' element={<Tasks />} />
                    <Route path='conversations'>
                      <Route path='' element={<Conversations />} />
                    </Route>
                    <Route
                      path='documents'
                      element={<Navigate to='/admin/contracts' replace />}
                    />
                    <Route path='home-sections'>
                      <Route path='' element={<HomeSectionsList />} />
                    </Route>
                    <Route path='custom-pages'>
                      <Route path='' element={<CustomPagesList />} />
                      <Route path='new' element={<CustomPagesForm />} />
                      <Route path=':id' element={<CustomPagesForm />} />
                    </Route>
                    <Route path='contract-templates'>
                      <Route path='' element={<ContractTemplatesList />} />
                    </Route>
                    <Route path='tutorials'>
                      <Route path='' element={<TutorialsList />} />
                      <Route path=':id' element={<TutorialDetail />} />
                    </Route>
                    <Route path='image-groups'>
                      <Route path='' element={<ImageGroupList />} />
                      <Route path=':id' element={<ImageGroupDetail />} />
                    </Route>
                    <Route path='proposals'>
                      <Route path='' element={<ProposalsList />} />
                      <Route path='pipeline' element={<ProposalsPipeline />} />
                      <Route path='compare' element={<ProposalsCompare />} />
                      <Route path=':id' element={<ProposalDetail />} />
                    </Route>
                    <Route path='contracts'>
                      <Route path='' element={<ContractsList />} />
                      <Route path=':id' element={<ContractDetail />} />
                    </Route>
                    <Route path='users'>
                      <Route path='' element={<UsersList />} />
                      <Route path='new' element={<UserCreate />} />
                      <Route path='edit/:id' element={<UserEdit />} />
                      <Route path=':id' element={<UserDetail />} />
                    </Route>
                    <Route path='acessos'>
                      <Route path='' element={<AccessList />} />
                      <Route path='summary/:id' element={<AccessSummary />} />
                      <Route path=':id' element={<AccessDetail />} />
                    </Route>
                    <Route
                      path='employees'
                      element={
                        <PrivateRoute
                          isAuthenticated={auth.isAdmin}
                          redirectTo='/admin'
                        >
                          <Outlet />
                        </PrivateRoute>
                      }
                    >
                      <Route path='' element={<EmployeesList />} />
                      <Route path=':id' element={<EmployeeDetail />} />
                    </Route>
                    {/* Origem automatica do lead pelo WhatsApp (ORIGEMWPP): so ADMIN */}
                    <Route
                      path='lead-origin-keywords'
                      element={
                        <PrivateRoute
                          isAuthenticated={auth.isAdmin}
                          redirectTo='/admin'
                        >
                          <LeadOriginKeywords />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path='email-templates'
                      element={
                        <PrivateRoute
                          isAuthenticated={auth.isAdmin}
                          redirectTo='/admin'
                        >
                          <EmailTemplatesPage />
                        </PrivateRoute>
                      }
                    />
                    <Route path='sectors'>
                      <Route path='' element={<SectorsList />} />
                    </Route>
                    <Route path='agency'>
                      <Route path='' element={<Agency />} />
                    </Route>
                    <Route path='customize'>
                      <Route path='' element={<Customize />} />
                    </Route>
                    {/* Integracoes sao so de administrador (o backend tambem exige ADMIN). */}
                    <Route
                      path='integration'
                      element={
                        <PrivateRoute
                          isAuthenticated={auth.isAdmin}
                          redirectTo='/admin'
                        >
                          <Outlet />
                        </PrivateRoute>
                      }
                    >
                      <Route path='api-keys' element={<ApiKeysList />} />
                      <Route path='webhooks' element={<WebhooksList />} />
                      <Route path='docs' element={<IntegrationDocs />} />
                      <Route path='portals' element={<PortaisIntegracao />} />
                      <Route path='gtm' element={<GtmIntegration />} />
                      <Route path='marcia' element={<MarciaIntegracao />} />
                    </Route>
                    <Route path='service-tickets'>
                      <Route path='' element={<ServiceTicketList />} />
                      <Route path=':id' element={<ServiceTicketDetail />} />
                    </Route>
                  </>
                ) : null}
                <Route path='my-tickets'>
                  <Route path='' element={<MyTickets />} />
                </Route>
                <Route path='*' element={<NotFound homePath='/admin' />} />
              </Route>
              {/*Contract Page*/}
              <Route
                path='/admin/contracts/:id/pdf'
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    redirectTo='/login'
                  >
                    <BackofficeRoute>
                      <ContractPDF />
                    </BackofficeRoute>
                  </PrivateRoute>
                }
              />
              {/*Site Pages*/}
              <Route path='/' element={<SiteLayout />}>
                <Route index element={<Home />} />
                <Route path='favorites' element={<Favorite />} />
                <Route path='property/:id' element={<PropertyClientDetail />} />
                <Route path='announce' element={<Announce />} />
                <Route path='cadastro' element={<Register />} />
                <Route
                  path='para-imobiliarias/cadastro'
                  element={<RegisterAgency />}
                />
                <Route path='know-us' element={<KnowUs />} />
                <Route path='terms' element={<Terms />} />
                <Route path='privacy' element={<Privacy />} />
                <Route path='p/:slug' element={<CustomPageView />} />
                <Route
                  path='empreendimentos'
                  element={<EmpreendimentosPublic />}
                />
                <Route
                  path='empreendimento/:slug'
                  element={<EmpreendimentoPublic />}
                />
              </Route>

              <Route
                path='/'
                element={<SiteLayout hideFooter={true} hideHeader={true} />}
              >
                <Route
                  path='login'
                  element={
                    <AnonymousRoute
                      isAuthenticated={isAuthenticated}
                      redirectTo={redirectRoute}
                    >
                      <Login />
                    </AnonymousRoute>
                  }
                />
                <Route path='change-password' element={<ChangePassword />} />
              </Route>

              <Route path='/explore' element={<SiteLayout />}>
                <Route index element={<Search />} />
              </Route>

              {/*Portal do Cliente*/}
              <Route
                path='/portal'
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    redirectTo='/login'
                  >
                    <PrivateRoute
                      isAuthenticated={auth.isCustomer}
                      redirectTo='/admin'
                    >
                      <PortalLayout />
                    </PrivateRoute>
                  </PrivateRoute>
                }
              >
                <Route index element={<PortalDashboard />} />
                <Route path='imoveis' element={<PortalRealEstates />} />
                <Route path='contratos' element={<PortalContracts />} />
                <Route
                  path='contratos/:id'
                  element={<PortalContractDetail />}
                />
                <Route path='propostas' element={<PortalProposals />} />
                <Route path='documentos' element={<PortalDocuments />} />
                <Route path='meus-dados' element={<PortalMyData />} />
              </Route>

              {/*Portal do Captador*/}
              <Route
                path='/captador'
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    redirectTo='/login'
                  >
                    <PrivateRoute
                      isAuthenticated={auth.isCaptador}
                      redirectTo='/admin'
                    >
                      <CaptadorLayout />
                    </PrivateRoute>
                  </PrivateRoute>
                }
              >
                <Route index element={<CaptadorDashboard />} />
                <Route path='novo-imovel' element={<CaptadorNovoImovel />} />
                <Route path='*' element={<NotFound homePath='/captador' />} />
              </Route>

              {/* Landing page de marketing da plataforma — sem auth, sem layout */}
              <Route path='/para-imobiliarias' element={<LandingPage />} />

              {/* Upload de fotos pelo celular — sem auth, sem layout.
                  Fica na raiz da plataforma, fora do basename de imobiliária:
                  quem abre o link é a câmera do corretor, e o álbum é resolvido
                  pelo token, não pelo slug. */}
              <Route path='/upload/:token' element={<PublicUpload />} />

              {/* Resposta do proprietário a uma proposta (#160) — link seguro enviado por
                  e-mail/WhatsApp; sem auth, sem layout */}
              <Route
                path='/proposta/responder/:token'
                element={<ProposalResponsePage />}
              />

              {/* Páginas públicas de assinatura — sem auth, sem layout */}
              <Route path='/sign/:token' element={<SignPage />} />
              <Route
                path='/sign/:token/pdf'
                element={<SignContractPdfPage />}
              />
              {/* Entrega do contrato assinado. Substitui o link direto para o S3 público
                  que ia no e-mail de conclusão. */}
              <Route
                path='/sign/:token/documento'
                element={<SignedDocumentPage />}
              />

              <Route
                path='*'
                element={
                  <Navigate
                    to={isAuthenticated ? getDefaultAuthenticatedRoute() : '/'}
                  />
                }
              />
            </Routes>
          </BrowserRouter>
        </Suspense>
      </TenantGuard>
    </ErrorBoundary>
  )
}
